import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { StorageSearchValues } from "types/StorageForms/Search";
import SearchFilter from "../SearchFilter";
import { DateButton } from "./Filter.styles";

// Same 20px breathing room before Apply as the Date popover's last block.
const Options = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

// Shipments with / without a customs declaration (q[has_customs_declarations]).
const FilterCustoms: FC<FormikProps<StorageSearchValues>> = ({
  initialValues,
  setFieldValue,
  values,
  handleSubmit,
  ...otherProps
}) => {
  const { t } = useTranslation("common");
  const value = values.hasCustomsDeclarations;
  const initialValue = initialValues.hasCustomsDeclarations;
  const [innerValue, setInnerValue] = useState(value);
  const isActive = value !== null;

  const title =
    value === null
      ? t("parcels.search.customs")
      : t(
          value
            ? "parcels.search.withCustoms"
            : "parcels.search.withoutCustoms",
        );

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const dropInnerValue = useCallback(() => setInnerValue(value), [value]);

  const applyInnerValue = useCallback(
    () => setFieldValue("hasCustomsDeclarations", innerValue),
    [innerValue, setFieldValue],
  );

  const setDefault = useCallback(() => {
    setFieldValue("hasCustomsDeclarations", initialValue);
    dropInnerValue();
  }, [dropInnerValue, initialValue, setFieldValue]);

  const selectWith = useCallback(() => setInnerValue(true), []);
  const selectWithout = useCallback(() => setInnerValue(false), []);

  return (
    <SearchFilter
      applyInnerValue={applyInnerValue}
      dropInnerValue={dropInnerValue}
      handleSubmit={handleSubmit}
      setDefault={setDefault}
      title={title}
      innerTitle={t("parcels.search.selectCustoms")}
      isActive={isActive}
      {...otherProps}
    >
      <Options>
        <DateButton
          type="button"
          onClick={selectWith}
          $isSelected={innerValue === true}
        >
          {t("parcels.search.withCustoms")}
        </DateButton>
        <DateButton
          type="button"
          onClick={selectWithout}
          $isSelected={innerValue === false}
        >
          {t("parcels.search.withoutCustoms")}
        </DateButton>
      </Options>
    </SearchFilter>
  );
};

export default memo(FilterCustoms);
